import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { Tools } from '@babylonjs/core/Misc/tools'
import { ComponentType } from '@lindberg/configuration'
import { replaceFinish } from '../ConfigurationInterpreter'
import Part from './Part'

export default class Front extends Part {
	public NosepadOutRight: TransformNode
	public NosepadOutLeft: TransformNode

	public TempleOutLeft: TransformNode
	public TempleOutRight: TransformNode

	constructor(parent: TransformNode, mirror: boolean = false) {
		super('FRONT', parent, mirror)

		this.attachmentPoints.set('NOSEPAD-OUT-RIGHT', this.NosepadOutRight = new TransformNode('NOSEPAD-OUT-RIGHT', this.getScene()))
		this.attachmentPoints.set('NOSEPAD-OUT-LEFT', this.NosepadOutLeft = new TransformNode('NOSEPAD-OUT-LEFT', this.getScene()))
		this.NosepadOutRight.parent = this.NosepadOutLeft.parent = this

		this.attachmentPoints.set('TEMPLE-OUT-RIGHT', this.TempleOutRight = new TransformNode('TEMPLE-OUT-RIGHT', this.getScene()))
		this.attachmentPoints.set('TEMPLE-OUT-LEFT', this.TempleOutLeft = new TransformNode('TEMPLE-OUT-LEFT', this.getScene()))
		this.TempleOutRight.parent = this.TempleOutLeft.parent = this
		this.TempleOutLeft.position = new Vector3(.065, .014, .014)
		this.TempleOutRight.position = new Vector3(-.065, .014, .014)
		this.NosepadOutLeft.position.x = .011
		this.NosepadOutRight.position.x = -.011
		this.NosepadOutLeft.rotation = new Vector3(Tools.ToRadians(-13.34), Tools.ToRadians(90), Tools.ToRadians(0))
		this.NosepadOutRight.rotation = new Vector3(Tools.ToRadians(-13.34), Tools.ToRadians(-90), Tools.ToRadians(0))
	}

	override get isPrecious(): boolean {
		return (['FRONT', 'UPPERRIM', 'BRIDGE', 'CLIP'] as ComponentType[]).some(x => this.config.components[x]?.color?.startsWith('AU750') || this.config.components[x]?.color?.startsWith('PT950'))
	}

	get isKidTeen(): boolean {
		return this.config.productline === 'RIMKID' || this.config.productline === 'RIMTEEN' || this.config.productline === '1000TEEN'
	}

	override get filename(): string {
		const modelType = this.isPrecious ? 'Precious' : this.isKidTeen ? 'Kids' : 'Titanium'
		const lensCurve = parseInt(this.config.components?.LENSES?.design ? this.config.components?.LENSES?.design?.match(/\d+/)[0] : '4')
		return this.config.components.MODEL ? `Fronts/${modelType}/${[this.config.components.MODEL.design + (lensCurve == 2 ? '-B2' : ''), this.config.components.MODEL.size].filter(Boolean).join(' ')}` : ''
	}

	override getMeshMaterialName(mesh: AbstractMesh) {
		let materialName: string
		let ao: boolean

		function matches(name: string, match: string) {
			return new RegExp(`^${name}(:?-(LEFT|RIGHT))?$`).test(match)
		}

		const front = /^(FRONT)(?:-(?:LEFT|RIGHT))?(-[A-Z0-9]{2,})?$/
		const bridge = /^(BRIDGE)(?:-(?:LEFT|RIGHT))?(-[A-Z0-9]{2,})?$/
		const clip = /^(CLIP)(?:-(?:LEFT|RIGHT))?(-[A-Z0-9]{2,})?$/

		if (front.test(mesh.name)) {
			const finish = mesh.name.match(front)[2]?.substring(1)
			return finish ? replaceFinish(this.config.components.FRONT.color, finish) : this.config.components.FRONT.color
		} else if (bridge.test(mesh.name)) {
			const finish = mesh.name.match(bridge)[2]?.substring(1)
			return finish ? replaceFinish(this.config.components.BRIDGE.color, finish) : this.config.components.BRIDGE.color
		} else if (clip.test(mesh.name)) {
			const finish = mesh.name.match(clip)[2]?.substring(1)
			return finish ? replaceFinish(this.config.components.CLIP.color, finish) : this.config.components.CLIP.color
		} else if (matches('UPPERRIM-DETAIL', mesh.name)) {
			materialName = this.config.components.UPPERRIM_DETAIL?.color || this.config.components.UPPERRIM.color
		} else if (matches('UPPERRIM', mesh.name)) {
			materialName = this.config.components.UPPERRIM.color
		} else if (matches('LOWERRIM', mesh.name)) {
			materialName = this.config.components.LOWERRIM.color
		} else if (matches('INNERRIM', mesh.name)) {
			materialName = this.config.components.INNERRIM.color
		} else if (matches('CROSSBAR', mesh.name)) {
			materialName = this.config.components.CROSSBAR?.color || this.config.components.FRONT?.color || this.config.components.UPPERRIM?.color
		} else if (matches('NOSEPADARM', mesh.name)) {
			materialName = this.config.components.NOSEPADARM.color
		} else if (matches('WIRE', mesh.name)) {
			materialName = this.config.components.WIRE.color
		} else if (matches('LENS', mesh.name) || matches('LENS-SURFACE', mesh.name) || matches('LENS-BACK', mesh.name)) {
			materialName = this.config.components.LENSES.color
		} else if (matches('LENS-EDGE', mesh.name)) {
			materialName = this.config.components.LENS_EDGE.color
		} else if (matches('LENS-GROOVE', mesh.name)) {
			materialName = this.config.components.GROOVE?.color || this.config.components.LENS_EDGE.color
		} else {
			throw (`Unexpected ${this.name} mesh: (${mesh.name})`)
		}
		return materialName
	}
}
